/**
 * @author       david.loehr@zumper.com (David Loehr)
 * @copyright    Copyright (c) 2022, Zumper
 * @description  Memoizes a selector that only accepts partial routeProps (no state)
 */

import { routePropsEqual } from './helpers/routePropsEqual'
import { MaybeRouteProps } from './types'

export type MapRouteToProps<
  Result,
  Params extends { [K in keyof Params]?: string } = { [K in string]?: string },
> = (routeProps: MaybeRouteProps<Params>) => Result

// Allows for selectors that accept routeProps instead of state
export const memoizeMaybeRouteProps = <
  Result,
  Params extends { [K in keyof Params]?: string } = { [K in string]?: string },
>(
  selector: MapRouteToProps<Result, Params>
) => {
  let prevRouteProps: MaybeRouteProps<Params> | undefined
  let prevValue: Result
  return (routeProps: MaybeRouteProps<Params>) => {
    if (!routePropsEqual(routeProps, prevRouteProps)) {
      prevRouteProps = routeProps
      prevValue = selector(routeProps)
    }
    return prevValue
  }
}
